/* eslint-disable camelcase */
import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ErrorIcon from '../../assets/images/icon_error_messages.gif';
import validationJSON from './schema';
import checkFieldToDisplay from './getfield';
import { Wrapper } from './css';
import FormField from '../design/shared/form/formField';
import request from '../utils/request';
import { channelOutletMapping } from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import Spinner from './spinner';
import ErrorMessage from './error';
import { displayLabel } from './utils';
import { sortedObjects } from '../utils/utils';

const propChannel = {
  id: PropTypes.number,
  name: PropTypes.string,
  description: PropTypes.string
};

const propProvince = {
  id: PropTypes.number,
  name: PropTypes.string
};

const propRole = {
  description: PropTypes.string,
  id: PropTypes.number,
  name: PropTypes.string
};

const propDealership = {
  id: PropTypes.number,
  name: PropTypes.string
};

const UserForm = ({
  user,
  roles,
  channels,
  provinces,
  dealershipList,
  submissionHandling
}) => {
  const localizedStrings = useLocalizedStringsContext();
  const TRANSLATION = localizedStrings.USERS.FORM;
  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(null);
  const {
    id,
    role: userRole,
    first_name,
    last_name,
    email,
    contact_number,
    provinces: userProvinces,
    sales_manager,
    sales_manager_email,
    cpms_id,
    channels: userChannels,
    outlet,
    role_id,
    dealership,
    channelOrgCode,
    xid,
    store_code
  } = user;

  const channelId =
    userChannels &&
    userChannels.length &&
    userChannels.map(({ id: userChannelId, description }) => {
      return { id: userChannelId, label: description };
    });

  const provinceId =
    userProvinces && userProvinces.length && userProvinces[0].id;
  const channelsIds = userChannels.map(item => item.id);
  const provincesIds = userProvinces.map(item => item.id);
  const sortedChannels = sortedObjects(channels, 'description');

  const formFieldList = [
    {
      label: displayLabel(TRANSLATION.LABEL.ROLE),
      type: 'select',
      name: 'roleId',
      id: 'role-id',
      defaultVal: role_id,
      list: roles,
      allowOptionNull: false,
      val: 'description'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.FIRST_NAME),
      type: 'text',
      name: 'firstName',
      placeholder: TRANSLATION.LABEL.FIRST_NAME
    },
    {
      label: displayLabel(TRANSLATION.LABEL.LAST_NAME),
      type: 'text',
      name: 'lastName',
      placeholder: TRANSLATION.LABEL.LAST_NAME
    },
    {
      label: displayLabel(TRANSLATION.LABEL.EMAIL),
      type: 'email',
      name: 'email',
      placeholder: TRANSLATION.LABEL.EMAIL,
      readOnly: true
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CONTACT_NUMBER),
      type: 'text',
      name: 'contactNumber',
      id: 'role-id',
      placeholder: TRANSLATION.LABEL.CONTACT_NUMBER
    },
    {
      label: displayLabel(TRANSLATION.LABEL.PROVINCE),
      type: 'select',
      name: 'province',
      id: 'province-id',
      defaultVal: provinceId,
      list: provinces,
      val: 'name'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.PROVINCE),
      type: 'multiCheckbox',
      name: 'provinceIds',
      id: 'province-ids',
      defaultVal: provincesIds,
      list: provinces,
      val: 'name'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CHANNEL),
      type: 'channelField',
      name: 'channelId',
      id: 'channel-id',
      defaultVal: channelId,
      list: sortedChannels,
      val: 'description'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.WHICH_CHANNELS_REPORT_TO_YOU),
      type: 'multiCheckbox',
      name: 'channelIds',
      id: 'channel-ids',
      defaultVal: channelsIds,
      list: sortedChannels,
      val: 'description'
    },
    {
      label: displayLabel(TRANSLATION.LABEL.DEALERSHIP),
      type: 'dealershipField',
      name: 'dealership',
      list: dealershipList
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CHANNEL_ORGANIZATION_CODE),
      type: 'text',
      name: 'channelOrgCode',
      placeholder: TRANSLATION.LABEL.CHANNEL_ORGANIZATION_CODE
    },
    {
      label: displayLabel(TRANSLATION.LABEL.STORE_CODE),
      type: 'text',
      name: 'storeCode',
      placeholder: TRANSLATION.LABEL.STORE_CODE
    },
    {
      label: displayLabel(TRANSLATION.LABEL.XID),
      type: 'text',
      name: 'xid',
      placeholder: TRANSLATION.LABEL.XID
    },
    {
      label: displayLabel(TRANSLATION.LABEL.CPMS_ID),
      type: 'text',
      name: 'cpmsId',
      placeholder: TRANSLATION.LABEL.CPMS_ID
    },
    {
      label: displayLabel(TRANSLATION.LABEL.OUTLET),
      type: 'text',
      name: 'outlet',
      placeholder: TRANSLATION.LABEL.OUTLET
    },
    {
      label: displayLabel(TRANSLATION.LABEL.MANAGER),
      type: 'text',
      name: 'salesManager',
      placeholder: TRANSLATION.LABEL.MANAGER
    },
    {
      label: displayLabel(TRANSLATION.LABEL.MANAGER_EMAIL),
      type: 'text',
      name: 'salesManagerEmail',
      placeholder: TRANSLATION.LABEL.MANAGER_EMAIL
    }
  ];

  const getDealershipInitialState = () => {
    const selectedDealershipObj = dealershipList.filter(
      row => row.id === +dealership
    );
    const transformedDealershipObj = selectedDealershipObj.map(row => ({
      id: row.id,
      label: row.name
    }));
    return transformedDealershipObj;
  };

  const initialState = {
    id,
    role: userRole,
    email,
    firstName: first_name,
    lastName: last_name,
    contactNumber: contact_number || '',
    province: provinceId || '',
    provinceIds: provincesIds,
    salesManager: sales_manager || '',
    salesManagerEmail: sales_manager_email || '',
    cpmsId: cpms_id || '',
    channelId: channelId || '',
    channelIds: channelsIds,
    outlet: outlet || '',
    dealership: getDealershipInitialState(),
    roleId: role_id || '',
    channelOrgCode: channelOrgCode || '',
    xid: xid || '',
    storeCode: store_code || ''
  };

  const handleSubmit = values => {
    const payloadKeys = checkFieldToDisplay(
      parseInt(values.roleId, 10),
      values
    );
    const payload = Object.keys(values)
      .filter(key => payloadKeys.includes(key))
      .reduce((obj, key) => {
        let newKey = key;
        let value = values[key];

        if (key === 'channelIds') newKey = 'channelId';
        else if (['provinceIds', 'province'].includes(key))
          newKey = 'provinces';

        if (key === 'province') value = [Number(values[key])];
        if (key === 'channelId') value = values[key].map(item => item.id);
        if (key === 'dealership') value = _.get(value, '0.id');
        return {
          ...obj,
          [newKey]: value
        };
      }, {});
    setError(null);
    setLoading(true);

    request
      .post(`/users/${values.id}/update`, payload)
      .then(() => {
        setLoading(false);
        submissionHandling();
      })
      .catch(err => {
        const {
          data: { error }
        } = err && err.response;
        setLoading(false);
        setError(error);
      });
  };

  const getFieldLabel = (role, { label, name }) => {
    if (['outlet', 'cpmsId'].includes(name) && [4, 11].includes(+role)) {
      return label.replace('*', '');
    }

    if (name === 'salesManager' && [2, 9].includes(+role)) {
      return `${TRANSLATION.LABEL.SALE_MANAGER}*`;
    }

    if (name === 'salesManagerEmail' && [2, 9].includes(+role)) {
      return `${TRANSLATION.LABEL.SALE_MANAGER_EMAIL}*`;
    }
    return label;
  };

  const getChannel = outletValue => {
    let result = '';
    Object.keys(channelOutletMapping).forEach(channel => {
      if (_.get(channelOutletMapping, channel, []).includes(outletValue))
        result = channels.filter(ch => ch.name === channel);
    });
    return (
      result &&
      result.map(({ id: cId, description }) => {
        return { id: cId, label: description };
      })
    );
  };

  return (
    <Wrapper>
      {loading ? (
        <Spinner />
      ) : (
        <React.Fragment>
          <div id="main-content" style={{ padding: '1rem 0' }}>
            <span className="frg-body-font-14px" style={{ fontWeight: '700' }}>
              {TRANSLATION.MESSAGE.FILL_DETAILS}
            </span>
          </div>
          {isError && (
            <ErrorMessage
              message={TRANSLATION.MESSAGE.SERVER_ERROR}
              isError={isError}
            />
          )}
          <div id="main-content-form">
            <Formik
              initialValues={initialState}
              validationSchema={validationJSON}
              onSubmit={values => {
                handleSubmit(values);
              }}
            >
              {({
                errors,
                touched,
                values,
                setFieldValue,
                setFieldTouched
              }) => (
                <Form>
                  <Field name="id" type="hidden" />
                  {formFieldList.map((field, index) => {
                    const key = `formField${index}`;

                    return checkFieldToDisplay(+values.roleId, values).map(
                      fieldName => {
                        const fieldData = { ...field };
                        fieldData.label = getFieldLabel(
                          values.roleId,
                          fieldData
                        );

                        if (!(field.name === fieldName)) return null;
                        return (
                          <FormField
                            key={key}
                            field={fieldData}
                            errors={errors}
                            touched={touched}
                            errorIcon={ErrorIcon}
                            channels={channels}
                            values={values}
                            setFieldValue={data => {
                              let value;
                              if (
                                ['multicheckbox', 'typehead'].includes(
                                  field.type
                                )
                              ) {
                                value = data.length > 0 ? data[0].id : '';
                                if (
                                  ['channelIds', 'provinceIds'].includes(
                                    field.name
                                  )
                                )
                                  value = data;
                              } else value = data;

                              setFieldValue(field.name, value, true);
                              if (field.name === 'outlet' && getChannel(value))
                                setFieldValue('channelId', getChannel(value));
                            }}
                            setFieldTouched={() =>
                              setFieldTouched(field.name, true)
                            }
                          />
                        );
                      }
                    );
                  })}
                  <div id="submit_button" style={{ marginTop: 20 }}>
                    {!loading && (
                      <span>
                        <button
                          type="submit"
                          className="frg-button button-wide color-green"
                        >
                          {TRANSLATION.BUTTON.SUBMIT}
                        </button>
                      </span>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </React.Fragment>
      )}
    </Wrapper>
  );
};

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    provinces: PropTypes.arrayOf(PropTypes.shape(propProvince)),
    status: PropTypes.string,
    contact_number: PropTypes.string,
    outlet: PropTypes.string,
    sales_rep_id: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.shape(propChannel)),
    role_id: PropTypes.number,
    cpms_id: PropTypes.string,
    role: PropTypes.shape(propRole),
    sales_manager: PropTypes.string,
    sales_manager_email: PropTypes.string,
    managerName: PropTypes.string,
    managerEmail: PropTypes.string,
    dealership: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string
    }),
    channelOrgCode: PropTypes.string,
    xid: PropTypes.string,
    store_code: PropTypes.string
  }).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape(propRole)).isRequired,
  channels: PropTypes.arrayOf(PropTypes.shape(propChannel)).isRequired,
  provinces: PropTypes.arrayOf(PropTypes.any).isRequired,
  dealershipList: PropTypes.arrayOf(PropTypes.shape(propDealership)).isRequired,
  submissionHandling: PropTypes.func.isRequired
};

export default UserForm;
